import { type CbhUserContext, getAnonymousLdUser } from "./cbhUserContext";

const DEFAULT_IDENTIFY_TIMEOUT_MS = 1000;

/**
 * Re-identifies the anonymous LaunchDarkly context with an unverified submitted
 * email. This attribute must only target progressive-disclosure UX, never
 * security-sensitive behavior. Keeping the context anonymous and omitting its
 * key preserves the SDK's persisted anonymous device key and stable rollout
 * bucketing. Marking email private allows evaluation while redacting it from
 * LaunchDarkly events and analytics.
 *
 * Resolves `true` once the flag re-evaluation completes, so the caller can
 * re-render and read the email-targeted flags. Resolves `false` if identify does
 * not complete within `timeoutMs` (or fails) — the caller then proceeds with its
 * current values; the identify keeps running in the background, so a later
 * submit and the next render still pick up the refreshed context. Never waits
 * longer than `timeoutMs`, so a stalled or rejected LaunchDarkly request can't
 * delay login.
 */
export async function identifyAnonymousLdUserWithEmail(
  props: IdentifyAnonymousLdUserWithEmailParams
): Promise<boolean> {
  const { ldClient, email, timeoutMs = DEFAULT_IDENTIFY_TIMEOUT_MS } = props;
  const context = {
    ...getAnonymousLdUser(),
    email,
    _meta: { privateAttributes: ["email"] },
  };

  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      ldClient.identify(context).then(() => true),
      new Promise<false>((resolve) => {
        timer = setTimeout(() => {
          resolve(false);
        }, timeoutMs);
      }),
    ]);
  } catch {
    // Identification is best-effort; login must continue if LaunchDarkly fails.
    return false;
  } finally {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  }
}

interface AnonymousLdIdentifyClient {
  identify(context: CbhUserContext): Promise<unknown>;
}

interface IdentifyAnonymousLdUserWithEmailParams {
  ldClient: AnonymousLdIdentifyClient;
  email: string;
  timeoutMs?: number;
}
